<template>
  <!--
    Zeitraum-Picker: ein Element statt 4 Dropdowns + 2 Datumsfeldern.
    Links Schnellauswahl (relative Zeiträume), rechts Jahr (Umschalter) · Quartal · Monat ·
    eigener Von–bis-Zeitraum. Die Setter-Logik ist unverändert aus der früheren Filterleiste.
  -->
  <div class="bdd mb-period">
    <!-- Auslöser wie die übrigen Filter-Buttons; aktiv hervorgehoben, weil immer ein Zeitraum gilt. -->
    <div class="bddbtn has">
      <button
        type="button"
        class="bddmain"
        :aria-expanded="isOpen ? 'true' : 'false'"
        aria-haspopup="dialog"
        @click="toggle"
      >
        <i class="far fa-calendar" aria-hidden="true" />
        <span>{{ activePeriodLabel }}</span>
      </button>
      <button type="button" class="bddcar" aria-label="Zeitraum wählen" @click="toggle">▾</button>
    </div>

    <template v-if="isOpen">
      <div class="bdd-back" @click="isOpen = false" />
      <div class="bddmenu mb-period__menu" role="dialog" aria-label="Zeitraum wählen">
        <!-- Schnellauswahl als Liste (wie in Jira/gängigen Datumsauswahlen) -->
        <div class="mb-period__presets" role="group" aria-label="Schnellauswahl">
          <p class="mb-period__caption">Schnellauswahl</p>
          <button
            v-for="option in PRESETS"
            :key="option.value"
            type="button"
            class="mb-period__preset"
            :class="{ on: filters.period === option.value }"
            :aria-pressed="filters.period === option.value ? 'true' : 'false'"
            @click="onPeriod(option.value)"
          >
            {{ option.label }}
          </button>
          <!-- Kein Datumsfilter: alles sehen, was vorhanden ist. -->
          <button
            type="button"
            class="mb-period__preset mb-period__preset--all"
            :class="{ on: filters.period === 'all' }"
            :aria-pressed="filters.period === 'all' ? 'true' : 'false'"
            @click="onPeriod('all')"
          >
            <i class="fas fa-infinity" aria-hidden="true" /> {{ PERIOD_LABELS.all }}
          </button>
        </div>

        <div class="mb-period__calendar">
          <!-- Jahr: Umschalter statt 7 einzelner Chips -->
          <div class="mb-period__section">
            <p class="mb-period__caption">Jahr</p>
            <div class="mb-period__year">
              <button type="button" class="mb-period__step" :disabled="displayYear <= minYear" aria-label="Vorheriges Jahr" @click="stepYear(-1)">
                <i class="fas fa-chevron-left" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="mb-period__year-value"
                :class="{ on: wholeYearActive }"
                :aria-pressed="wholeYearActive ? 'true' : 'false'"
                :title="`Ganzes Jahr ${displayYear}`"
                @click="onYear(String(displayYear))"
              >
                {{ displayYear }}
              </button>
              <button type="button" class="mb-period__step" :disabled="displayYear >= maxYear" aria-label="Nächstes Jahr" @click="stepYear(1)">
                <i class="fas fa-chevron-right" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div class="mb-period__section">
            <p class="mb-period__caption">Quartal {{ displayYear }}</p>
            <div class="mb-period__grid mb-period__grid--4">
              <button
                v-for="quarter in [1, 2, 3, 4]"
                :key="quarter"
                type="button"
                class="mb-period__chip"
                :class="{ on: isActiveYear && String(filters.quarter) === String(quarter) }"
                @click="onQuarter(isActiveYear && String(filters.quarter) === String(quarter) ? '' : String(quarter))"
              >
                Q{{ quarter }}
              </button>
            </div>
          </div>

          <div class="mb-period__section">
            <p class="mb-period__caption">Monat {{ displayYear }}</p>
            <div class="mb-period__grid mb-period__grid--6">
              <button
                v-for="(name, index) in MONTH_NAMES"
                :key="name"
                type="button"
                class="mb-period__chip"
                :class="{ on: isActiveYear && String(filters.month) === String(index + 1) }"
                :title="`${name} ${displayYear}`"
                @click="onMonth(isActiveYear && String(filters.month) === String(index + 1) ? '' : String(index + 1))"
              >
                {{ name.slice(0, 3) }}
              </button>
            </div>
          </div>

          <div class="mb-period__section">
            <p class="mb-period__caption">Eigener Zeitraum</p>
            <div class="mb-period__range">
              <label>
                <span>Von</span>
                <input class="mb-date" :class="{ 'is-empty': !filters.date_from }" type="date" :value="filters.date_from" @change="onFrom" />
              </label>
              <label>
                <span>Bis</span>
                <input class="mb-date" :class="{ 'is-empty': !filters.date_to }" type="date" :value="filters.date_to" @change="onTo" />
              </label>
            </div>
          </div>
        </div>

        <div class="mb-period__foot">
          <span class="mb-period__summary">Aktiv: <strong>{{ activePeriodLabel }}</strong></span>
          <button type="button" class="jr-button" @click="isOpen = false">Fertig</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { MONTH_NAMES, PERIOD_LABELS } from '../composables/formatters';
import { useMarketingFilters } from '../composables/useMarketingFilters';

const { filters, setFilter, activePeriodLabel } = useMarketingFilters();
const isOpen = ref(false);

const PRESETS = [
  { value: 'last_7_days', label: PERIOD_LABELS.last_7_days },
  { value: 'last_30_days', label: PERIOD_LABELS.last_30_days },
  { value: 'current_month', label: PERIOD_LABELS.current_month },
  { value: 'current_quarter', label: PERIOD_LABELS.current_quarter }
];

// Gleicher Jahresbereich wie bisher: 5 Jahre zurück bis 1 Jahr voraus (+ ggf. gewähltes Jahr).
const currentYear = new Date().getFullYear();
const minYear = computed(() => Math.min(currentYear - 5, Number(filters.year) || currentYear));
const maxYear = computed(() => Math.max(currentYear + 1, Number(filters.year) || currentYear));

// Angezeigtes Jahr im Umschalter; Quartal und Monat beziehen sich darauf.
const displayYear = ref(Number(filters.year) || currentYear);
watch(() => filters.year, (year) => {
  if (year) displayYear.value = Number(year);
});

const isActiveYear = computed(() => String(filters.year) === String(displayYear.value));
const wholeYearActive = computed(() => isActiveYear.value && !filters.quarter && !filters.month && !filters.period);

function toggle() {
  if (!isOpen.value) displayYear.value = Number(filters.year) || currentYear;
  isOpen.value = !isOpen.value;
}

// Ist bereits ein Jahr gefiltert, wechselt der Umschalter den Filter mit (Quartal/Monat bleiben erhalten);
// sonst ändert er nur die Anzeige für Quartal und Monat.
function stepYear(delta) {
  const next = Math.min(maxYear.value, Math.max(minYear.value, displayYear.value + delta));
  displayYear.value = next;
  if (filters.year) onYear(String(next));
}

function ensureYear() {
  return String(displayYear.value);
}

function onYear(value) {
  setFilter({
    year: value,
    period: value ? '' : (filters.period || 'last_30_days'),
    date_from: '',
    date_to: '',
    month: value ? filters.month : '',
    quarter: value ? filters.quarter : ''
  });
}

function onQuarter(value) {
  setFilter({
    quarter: value,
    month: '',
    year: value ? ensureYear() : filters.year,
    period: '',
    date_from: '',
    date_to: ''
  });
}

function onMonth(value) {
  setFilter({
    month: value,
    quarter: '',
    year: value ? ensureYear() : filters.year,
    period: '',
    date_from: '',
    date_to: ''
  });
}

function onPeriod(value) {
  setFilter({ period: value, date_from: '', date_to: '', year: '', month: '', quarter: '' });
  isOpen.value = false;
}

function onFrom(event) {
  setFilter({ date_from: event.target.value, period: '' });
}

function onTo(event) {
  setFilter({ date_to: event.target.value, period: '' });
}

function onKey(event) {
  if (event.key === 'Escape') isOpen.value = false;
}

onMounted(() => window.addEventListener('keydown', onKey));
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
</script>
